import { Box, Button, FlexLayout, Icon } from '@deity-io/portal-ui';
import StructurePicker from 'components/FieldEditorControls/StructurePicker';
import SchemaView from 'components/SchemaEditor/SchemaView';
import { CreateEditStructureModal } from 'components/Structures';
import { RequestExplorerActionTypes, RequestExplorerContext } from 'contexts/RequestExplorer';
import { CACHE_FIRST } from 'contracts/constants';
import type { FC } from 'react';
import { useContext, useState } from 'react';
import {
  useDetectSchemaMutation,
  useGetModelsByPackageVersionQuery,
  useParseSchemaMutation,
  useValidateToSchemaMutation
} from 'services/graphql/generated/hooks';
import { v4 } from 'uuid';
import ValidationReport from './ValidationReport';

interface ResponseStructureProps {
  locked?: boolean;
}
const ResponseStructure: FC<ResponseStructureProps> = ({ locked }) => {
  const [selectedSchemaField, setSelectedSchemaField] = useState<Array<string>>([]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newStructureKey, setNewStructureKey] = useState<string>(v4());
  const [detectSchema, { data: detectSchemaResult, loading: detectingSchema }] = useDetectSchemaMutation();
  const [validateToSchema, { data: schemaValidationResult, loading: validatingSchema }] = useValidateToSchemaMutation();
  const [parseData, { data: parseDataResult, loading: parsingData }] = useParseSchemaMutation();

  const ctx = useContext(RequestExplorerContext);

  const { data, loading, refetch } = useGetModelsByPackageVersionQuery(
    CACHE_FIRST && {
      variables: {
        offset: 0,
        limit: 100,
        packageVersionUuid: ctx?.state.packageVersionUuid
      },
      skip: !ctx?.state.packageVersionUuid
    }
  );

  if (!ctx) {
    // eslint-disable-next-line no-console
    console.error('RequestExplorerContextProvider required');

    return <>RequestExplorerContextProvider required</>;
  }
  let schema: string = '';
  if (ctx.state.responseStructure) {
    const firstKey = Object.keys(ctx.state.responseStructure)[0];
    schema = JSON.stringify(ctx.state.responseStructure);
  }

  return (
    // <Box sx={{ padding: '10px' }}>
    <FlexLayout flexDirection="column" sx={{ padding: '10px' }}>
      <FlexLayout key="selectStructure" flexDirection="row" alignItems="center">
        <StructurePicker
          packageVersionUuid={ctx.state.packageVersionUuid || ''}
          selectedValue={ctx.state.responseStructureUuid}
          onChange={(val, structure) => {
            ctx?.dispatch({
              type: RequestExplorerActionTypes.UPDATE_RESPONSE_STRUCTURE,
              payload: { uuid: val, structure }
            });
          }}
        />
        <Button disabled={validatingSchema} onClick={() => {}} endIcon={<Icon icon="arrowRight" fontSize="large" />}>
          Load
        </Button>
      </FlexLayout>
      <FlexLayout key="detectStructure" flexDirection="row" alignItems="center">
        <Button
          disabled={validatingSchema}
          onClick={() => {
            detectSchema({
              variables: {
                data: ctx.state.response?.body || '',
                key: 'test'
              }
            }).then(detected => {
              if (detected.data) {
                ctx?.dispatch({
                  type: RequestExplorerActionTypes.UPDATE_RESPONSE_STRUCTURE,
                  payload: { uuid: undefined, structure: detected.data.detectDataSchema }
                });
              }
            });
          }}
          endIcon={<Icon icon="arrowRight" fontSize="large" />}
        >
          Detect Schema from response body
        </Button>
      </FlexLayout>
      {ctx.state.responseStructure && (
        <Box
          width="full"
          style={{
            minHeight: '100px',
            borderStyle: 'solid',
            borderColor: '#D4D4D4',
            borderWidth: '1px'
          }}
        >
          <SchemaView
            key="responseStructure"
            schema={ctx.state.responseStructure ? ctx.state.responseStructure : undefined}
            field="responseStructure"
            selectedField={selectedSchemaField}
            onSelect={val => {
              setSelectedSchemaField(val);
            }}
          />
        </Box>
      )}
      {ctx.state.responseStructure && (
        <FlexLayout key="validate" flexDirection="row" alignItems="center">
          <Button
            disabled={validatingSchema}
            onClick={() => {
              validateToSchema({
                variables: {
                  data: ctx.state.response?.body || '',
                  schema
                }
              }).catch(er => {
                // eslint-disable-next-line no-console
                console.log('Mutation Failed', er);
              });
            }}
            endIcon={<Icon icon="arrowRight" fontSize="large" />}
          >
            Validate body to schema
          </Button>
          <Button
            sx={{ marginLeft: '10px' }}
            disabled={validatingSchema}
            onClick={() => {
              setIsModalOpen(true);
            }}
            endIcon={<Icon icon="save" fontSize="large" />}
          >
            Save as
          </Button>
        </FlexLayout>
      )}
      {schemaValidationResult && (
        <Box
          width="full"
          style={{
            minHeight: '100px',
            borderStyle: 'solid',
            borderColor: '#D4D4D4',
            borderWidth: '1px'
          }}
        >
          {/* {JSON.stringify(schemaValidationResult.validateToSchema, null, 4)} */}
          <ValidationReport report={schemaValidationResult.validateToSchema} />
        </Box>
      )}
      {ctx.state.responseStructure && (
        <FlexLayout key="parse" flexDirection="row" alignItems="center">
          <Button
            disabled={parsingData}
            onClick={() => {
              parseData({
                variables: {
                  data: ctx.state.response?.body || '',
                  schema
                }
              });
            }}
            endIcon={<Icon icon="arrowRight" fontSize="large" />}
          >
            Parse response body to Schema
          </Button>
        </FlexLayout>
      )}
      {parseDataResult && (
        <Box
          width="full"
          style={{
            minHeight: '100px',
            borderStyle: 'solid',
            borderColor: '#D4D4D4',
            borderWidth: '1px'
          }}
        >
          {JSON.stringify(parseDataResult, null, 4)}
        </Box>
      )}
      {ctx?.state.packageVersionUuid && (
        <CreateEditStructureModal
          key={newStructureKey}
          isOpen={isModalOpen}
          versionUuid={ctx.state.packageVersionUuid}
          setOpenState={setIsModalOpen}
          schema={ctx.state.responseStructure}
          onPackageCreated={() => {
            refetch({
              offset: 0,
              limit: 100,
              packageVersionUuid: ctx?.state.packageVersionUuid
            });
            setNewStructureKey(v4());
          }}
        />
      )}
    </FlexLayout>
  );
};

export default ResponseStructure;
